import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { fetchTeamAttendanceAnalytics, fetchDayActivityTimeline } from '../../services/attendanceService';
import DayActivityTimelineModal from './DayActivityTimelineModal';

const formatDateInput = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const minutesToHours = (mins) => {
  const n = Number(mins) || 0;
  return Math.round((n / 60) * 10) / 10;
};

const formatMinutesLabel = (mins) => {
  const n = Math.max(0, Number(mins) || 0);
  if (n < 60) return `${n}m`;
  const h = Math.floor(n / 60);
  const m = n % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
};

const ChartCard = ({ title, subtitle, children }) => (
  <section className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
    <div className="px-4 sm:px-5 pt-4 pb-2">
      <h3 className="text-sm font-bold text-gray-900 dark:text-white">{title}</h3>
      {subtitle && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{subtitle}</p>
      )}
    </div>
    <div className="h-64 sm:h-72 px-2 pb-3">{children}</div>
  </section>
);

const TeamAttendanceAnalyticsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const userId = searchParams.get('userId') || '';
  const userNameParam = searchParams.get('userName') || '';

  const today = useMemo(() => formatDateInput(new Date()), []);
  const defaultStart = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 29);
    return formatDateInput(d);
  }, []);

  const [startDate, setStartDate] = useState(searchParams.get('startDate') || defaultStart);
  const [endDate, setEndDate] = useState(searchParams.get('endDate') || today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(Boolean(userId));
  const [error, setError] = useState('');
  const [dayTimeline, setDayTimeline] = useState(null);
  const [openingTimeline, setOpeningTimeline] = useState(false);

  useEffect(() => {
    if (!userId) {
      setError('Missing user. Open this page from Team Attendance.');
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const result = await fetchTeamAttendanceAnalytics(userId, startDate, endDate);
        if (!cancelled) setData(result);
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.error || err?.message || 'Failed to load analytics');
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [userId, startDate, endDate]);

  const applyPreset = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - (days - 1));
    const nextStart = formatDateInput(start);
    const nextEnd = formatDateInput(end);
    setStartDate(nextStart);
    setEndDate(nextEnd);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('startDate', nextStart);
      next.set('endDate', nextEnd);
      return next;
    });
  };

  const applyCustomRange = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('startDate', startDate);
      next.set('endDate', endDate);
      return next;
    });
  };

  const openDayTimeline = async (day) => {
    if (!userId || !day?.date) return;
    setOpeningTimeline(true);
    setError('');
    try {
      const timeline = await fetchDayActivityTimeline(day.date, userId);
      setDayTimeline(timeline);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to open day timeline');
    } finally {
      setOpeningTimeline(false);
    }
  };

  const chartRows = useMemo(() => {
    const days = data?.days || [];
    return days.map((day) => ({
      date: day.date,
      label: day.dateLabel || day.date,
      shortLabel: (day.dateLabel || day.date || '').replace(/ \d{4}$/, ''),
      productivity: day.productivityPercent ?? 0,
      totalHours: minutesToHours(day.totalTimeSpentMinutes),
      breakHours: minutesToHours(day.totalBreakMinutes),
      leaveHours: minutesToHours(day.leaveMinutes),
      tasksCompleted: day.tasksCompletedCount ?? 0,
      workingHours: minutesToHours(day.workingTimeMinutes),
    }));
  }, [data]);

  const summary = useMemo(() => {
    const days = data?.days || [];
    const withWork = days.filter((d) => (d.totalTimeSpentMinutes || 0) > 0 || d.status === 'CHECKED_IN' || d.status === 'LOGGED_OUT');
    const prodDays = withWork.filter((d) => d.productivityPercent != null);
    const avgProductivity = prodDays.length
      ? Math.round(prodDays.reduce((s, d) => s + (d.productivityPercent || 0), 0) / prodDays.length)
      : 0;
    const totalTime = days.reduce((s, d) => s + (Number(d.totalTimeSpentMinutes) || 0), 0);
    const totalBreak = days.reduce((s, d) => s + (Number(d.totalBreakMinutes) || 0), 0);
    const totalTasks = days.reduce((s, d) => s + (Number(d.tasksCompletedCount) || 0), 0);
    const leaveDays = days.filter((d) => d.onLeave).length;
    return { avgProductivity, totalTime, totalBreak, totalTasks, leaveDays, workDays: withWork.length };
  }, [data]);

  const displayName = data?.userName || userNameParam || 'Teammate';
  const tooltipStyle = {
    backgroundColor: 'rgba(255,255,255,0.98)',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    fontSize: '12px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50/80 via-gray-50 to-white dark:from-gray-950 dark:via-gray-900 dark:to-gray-900 text-gray-900 dark:text-gray-100">
      <header className="sticky top-0 z-20 border-b border-emerald-100/80 dark:border-gray-800 bg-white/90 dark:bg-gray-900/90 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-400">
              Team Attendance Analytics
            </p>
            <h1 className="text-xl sm:text-2xl font-bold truncate mt-0.5">{displayName}</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Table + day-wise charts · {startDate} → {endDate}
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">From</label>
              <input
                type="date"
                value={startDate}
                max={endDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-2.5 py-2 outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-1">To</label>
              <input
                type="date"
                value={endDate}
                min={startDate}
                max={today}
                onChange={(e) => setEndDate(e.target.value)}
                className="text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-2.5 py-2 outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
              />
            </div>
            <button
              type="button"
              onClick={applyCustomRange}
              className="text-sm font-bold px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700"
            >
              Apply
            </button>
            <button
              type="button"
              onClick={() => applyPreset(7)}
              className="text-sm font-semibold px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              7 days
            </button>
            <button
              type="button"
              onClick={() => applyPreset(30)}
              className="text-sm font-semibold px-3 py-2 rounded-lg border border-emerald-300 dark:border-emerald-700 text-emerald-800 dark:text-emerald-200 bg-emerald-50 dark:bg-emerald-950/40 hover:bg-emerald-100 dark:hover:bg-emerald-900/40"
            >
              30 days
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-5 space-y-5">
        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 dark:bg-red-950/40 dark:border-red-900 px-4 py-3 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        {loading ? (
          <div className="py-20 text-center text-sm text-gray-500 dark:text-gray-400">Loading analytics…</div>
        ) : data ? (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
              {[
                { label: 'Avg productivity', value: `${summary.avgProductivity}%` },
                { label: 'Total time spent', value: formatMinutesLabel(summary.totalTime) },
                { label: 'Total break time', value: formatMinutesLabel(summary.totalBreak) },
                { label: 'Tasks completed', value: summary.totalTasks },
                { label: 'Leave days', value: summary.leaveDays },
              ].map((card) => (
                <div
                  key={card.label}
                  className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3.5 py-3"
                >
                  <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{card.label}</p>
                  <p className="text-xl font-bold mt-1 tabular-nums">{card.value}</p>
                </div>
              ))}
            </div>

            <section className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
              <div className="px-4 sm:px-5 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                <div>
                  <h2 className="text-sm font-bold text-gray-900 dark:text-white">Daily attendance table</h2>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Productivity, time spent, breaks, tasks and leave
                  </p>
                </div>
                <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400">
                  {summary.workDays} work day{summary.workDays === 1 ? '' : 's'}
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-xs sm:text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-900/70 text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    <tr>
                      <th className="px-3 py-2.5 font-bold">Date</th>
                      <th className="px-3 py-2.5 font-bold">Status</th>
                      <th className="px-3 py-2.5 font-bold">Check-in</th>
                      <th className="px-3 py-2.5 font-bold">Logout</th>
                      <th className="px-3 py-2.5 font-bold text-right">Productivity</th>
                      <th className="px-3 py-2.5 font-bold text-right">Total time</th>
                      <th className="px-3 py-2.5 font-bold text-right">Break</th>
                      <th className="px-3 py-2.5 font-bold text-right">Working</th>
                      <th className="px-3 py-2.5 font-bold text-right">Tasks</th>
                      <th className="px-3 py-2.5 font-bold">Leave</th>
                      <th className="px-3 py-2.5 font-bold text-center">Timeline</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700/80">
                    {[...(data.days || [])].reverse().map((day) => (
                      <tr key={day.date} className="hover:bg-gray-50/80 dark:hover:bg-gray-900/40">
                        <td className="px-3 py-2.5 whitespace-nowrap">
                          <p className="font-semibold text-gray-900 dark:text-white">{day.dateLabel || day.date}</p>
                          <p className="text-[11px] text-gray-500">{day.dayLabel}</p>
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap text-gray-600 dark:text-gray-300">
                          {day.status === 'NO_ENTRY' ? '—' : (day.status || '—').replace('_', ' ')}
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap">{day.checkInTime || '—'}</td>
                        <td className="px-3 py-2.5 whitespace-nowrap">{day.checkOutTime || '—'}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums font-semibold">
                          {day.productivityPercent != null ? `${day.productivityPercent}%` : '—'}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums">
                          {day.totalTimeSpent || formatMinutesLabel(day.totalTimeSpentMinutes)}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums">
                          {day.totalBreakTime || formatMinutesLabel(day.totalBreakMinutes)}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums">
                          {day.workingTime || formatMinutesLabel(day.workingTimeMinutes)}
                        </td>
                        <td className="px-3 py-2.5 text-right tabular-nums font-semibold">
                          {day.tasksCompletedCount ?? 0}
                        </td>
                        <td className="px-3 py-2.5 whitespace-nowrap">
                          {day.onLeave ? (
                            <span className="inline-flex flex-col">
                              <span className="font-semibold text-amber-700 dark:text-amber-300">
                                {day.leaveLabel || 'Leave'}
                              </span>
                              <span className="text-[10px] text-gray-500">
                                {formatMinutesLabel(day.leaveMinutes)}
                                {day.leaveStatus ? ` · ${day.leaveStatus}` : ''}
                              </span>
                            </span>
                          ) : '—'}
                        </td>
                        <td className="px-3 py-2.5 text-center">
                          <button
                            type="button"
                            onClick={() => openDayTimeline(day)}
                            disabled={openingTimeline || day.status === 'NO_ENTRY'}
                            className="inline-flex items-center justify-center px-2 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                            title="Open day timeline"
                            aria-label={`Open day timeline for ${day.dateLabel || day.date}`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h10M4 18h7" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <ChartCard title="Productivity (day-wise)" subtitle="Percentage completed vs planned">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartRows} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="prodFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.35} />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} strokeOpacity={0.4} />
                    <XAxis dataKey="shortLabel" tick={{ fontSize: 10 }} minTickGap={28} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} width={36} />
                    <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`${v}%`, 'Productivity']} />
                    <Area type="monotone" dataKey="productivity" stroke="#059669" strokeWidth={2} fill="url(#prodFill)" />
                  </AreaChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="Total time available (day-wise)" subtitle="Hours checked in / on clock">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartRows} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} strokeOpacity={0.4} />
                    <XAxis dataKey="shortLabel" tick={{ fontSize: 10 }} minTickGap={28} />
                    <YAxis tick={{ fontSize: 10 }} width={36} />
                    <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`${v}h`, 'Total time']} />
                    <Bar dataKey="totalHours" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="Break time (day-wise)" subtitle="Short breaks in hours">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartRows} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="breakFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.35} />
                        <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} strokeOpacity={0.4} />
                    <XAxis dataKey="shortLabel" tick={{ fontSize: 10 }} minTickGap={28} />
                    <YAxis tick={{ fontSize: 10 }} width={36} />
                    <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`${v}h`, 'Break']} />
                    <Area type="monotone" dataKey="breakHours" stroke="#d97706" strokeWidth={2} fill="url(#breakFill)" />
                  </AreaChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="Leave (day-wise)" subtitle="Leave hours (apply + date-range requests)">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartRows} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} strokeOpacity={0.4} />
                    <XAxis dataKey="shortLabel" tick={{ fontSize: 10 }} minTickGap={28} />
                    <YAxis tick={{ fontSize: 10 }} width={36} />
                    <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`${v}h`, 'Leave']} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="leaveHours" name="Leave hours" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>
          </>
        ) : null}
      </main>

      {dayTimeline && (
        <DayActivityTimelineModal
          timeline={dayTimeline}
          onClose={() => setDayTimeline(null)}
          onChangeDate={async (nextDate) => {
            if (!userId) return;
            const timeline = await fetchDayActivityTimeline(nextDate, userId);
            setDayTimeline(timeline);
          }}
        />
      )}
    </div>
  );
};

export default TeamAttendanceAnalyticsPage;
